/**
 * Generation Section
 * Sub-component of PreferencesModal for resume section accordion with generation type cycling.
 * Supports section reordering via up/down arrows. Coverletter is always last and not moveable.
 */
import { Brain, Settings, XCircle, SlidersHorizontal, ChevronUp, ChevronDown } from 'lucide-react';
import type { UserPreferences, SubcomponentType, GenerationType } from '../../types';
import { GENERATION_TYPE_RESTRICTIONS, DEFAULT_SECTION_ORDER } from '../../types';
import { SUBCOMPONENT_LABELS } from '../../constants';

export interface GenerationSectionProps {
  preferences: UserPreferences;
  sectionNamesMap: Record<string, string>;
  sectionOrder: SubcomponentType[];
  onPreferencesChange: (updated: UserPreferences) => void;
  onSectionNamesMapChange: (updated: Record<string, string>) => void;
  onSectionOrderChange: (order: SubcomponentType[]) => void;
}

const typeIcons: Record<GenerationType, { icon: React.ReactNode; label: string; className: string }> = {
  embedding: { icon: <Brain className="w-4 h-4 mr-1" />, label: 'AI (Match)', className: 'bg-purple-100 text-purple-700' },
  generate: { icon: <SlidersHorizontal className="w-4 h-4 mr-1" />, label: 'AI (Generate)', className: 'bg-purple-200 text-purple-800' },
  manual: { icon: <Settings className="w-4 h-4 mr-1" />, label: 'Manual', className: 'bg-gray-200 text-gray-700' },
  omit: { icon: <XCircle className="w-4 h-4 mr-1" />, label: 'Omit', className: 'bg-red-100 text-red-700' },
};

export function GenerationSection({ preferences, sectionNamesMap, sectionOrder, onPreferencesChange, onSectionNamesMapChange, onSectionOrderChange }: GenerationSectionProps) {
  // Resume sections (reorderable) — excludes contact (always first) and coverletter (always last)
  const resumeSections = sectionOrder.filter(s => s !== 'coverletter' && s !== 'contact');

  // Ensure sectionOrder contains all DEFAULT_SECTION_ORDER items (in case new sections were added)
  const validResumeSections = resumeSections.filter(s => (DEFAULT_SECTION_ORDER as readonly SubcomponentType[]).includes(s));
  const resumeSectionsSet = new Set<SubcomponentType>(validResumeSections);
  const allResumeSections: SubcomponentType[] = [
    ...validResumeSections,
    ...DEFAULT_SECTION_ORDER.filter(s => s !== 'contact' && !resumeSectionsSet.has(s)),
  ];

  function cycleGenerationType(component: SubcomponentType) {
    const key = `default_gen_${component}` as keyof UserPreferences;
    const current = preferences[key] as GenerationType;
    const restrictions = GENERATION_TYPE_RESTRICTIONS[component];
    const allowedTypes: GenerationType[] = restrictions || ['manual', 'embedding', 'generate', 'omit'];

    const currentIndex = allowedTypes.indexOf(current);
    const nextIndex = (currentIndex + 1) % allowedTypes.length;
    const newValue = allowedTypes[nextIndex];

    onPreferencesChange({ ...preferences, [key]: newValue });
  }

  function updateSectionName(component: SubcomponentType, value: string) {
    const updated = { ...sectionNamesMap };
    if (value.trim() === '') {
      delete updated[component];
    } else {
      updated[component] = value;
    }
    onSectionNamesMapChange(updated);
    const jsonStr = Object.keys(updated).length > 0 ? JSON.stringify(updated) : undefined;
    onPreferencesChange({ ...preferences, section_names: jsonStr as string | undefined });
  }

  function moveSection(index: number, direction: 'up' | 'down') {
    const newOrder = [...allResumeSections];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newOrder.length) return;
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    onSectionOrderChange(newOrder);
  }

  function renderRow(component: SubcomponentType, index: number, isResume: boolean) {
    const key = `default_gen_${component}` as keyof UserPreferences;
    const currentType = (preferences[key] || 'embedding') as GenerationType;
    const typeConfig = typeIcons[currentType];

    return (
      <div
        key={component}
        data-help-target={`mainapp.user.settings.resumesections.${component}`}
        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
      >
        {isResume && (
          <div className="flex flex-col gap-0.5 flex-shrink-0">
            <button
              onClick={() => moveSection(index, 'up')}
              disabled={index === 0}
              className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-25 disabled:cursor-not-allowed"
              aria-label={`Move ${SUBCOMPONENT_LABELS[component]} up`}
            >
              <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
            </button>
            <button
              onClick={() => moveSection(index, 'down')}
              disabled={index === allResumeSections.length - 1}
              className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-25 disabled:cursor-not-allowed"
              aria-label={`Move ${SUBCOMPONENT_LABELS[component]} down`}
            >
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>
        )}
        <input
          type="text"
          value={sectionNamesMap[component] || ''}
          onChange={(e) => updateSectionName(component, e.target.value)}
          placeholder={SUBCOMPONENT_LABELS[component]}
          className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
        />
        <button
          onClick={() => cycleGenerationType(component)}
          data-help-target={`mainapp.user.settings.resumesections.${component}.gentype`}
          className={`flex items-center px-3 py-1 rounded-full text-sm whitespace-nowrap flex-shrink-0 ${typeConfig.className}`}
        >
          {typeConfig.icon}
          {typeConfig.label}
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      <p className="text-xs text-gray-500 mb-3">
        Configure display names, default generation types, and ordering for each resume section.
      </p>
      {/* Contact is always first */}
      <p className="text-xs text-gray-500">Contacts (always first)</p>
      {renderRow('contact', -1, false)}
      <div className="border-t border-gray-300 my-2" />

      {allResumeSections.map((component, index) => renderRow(component, index, true))}

      {/* Separator before coverletter */}
      <div className="border-t border-gray-300 my-2" />
      <p className="text-xs text-gray-500">Cover Letter (always last)</p>
      {renderRow('coverletter', -1, false)}
    </div>
  );
}
